/**
 * MintCountdownTimer
 *
 * Displays a countdown timer for upcoming mint phases.
 */

import React, { useState, useEffect } from 'react';

export interface MintCountdownTimerProps {
  targetDate: Date;
  label: string;
}

interface TimeLeft {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}

function getTimeLeft(target: Date): TimeLeft {
  const now = new Date();
  const diff = target.getTime() - now.getTime();

  if (diff <= 0) {
    return { days: 0, hours: 0, minutes: 0, seconds: 0 };
  }

  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((diff % (1000 * 60)) / 1000);

  return { days, hours, minutes, seconds };
}

export const MintCountdownTimer: React.FC<MintCountdownTimerProps> = ({ targetDate, label }) => {
  const [timeLeft, setTimeLeft] = useState(getTimeLeft(targetDate));

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(getTimeLeft(targetDate));
    }, 1000);

    return () => clearInterval(timer);
  }, [targetDate]);

  const units: { value: string; label: string }[] = [];
  if (timeLeft.days > 0) {
    units.push({ value: String(timeLeft.days).padStart(2, '0'), label: 'Days' });
  }
  units.push({ value: String(timeLeft.hours).padStart(2, '0'), label: 'Hours' });
  units.push({ value: String(timeLeft.minutes).padStart(2, '0'), label: 'Minutes' });
  units.push({ value: String(timeLeft.seconds).padStart(2, '0'), label: 'Seconds' });

  // Build compact text for xs screens: "01d 05h 46m 12s"
  const compactText = units.map(u => `${u.value}${u.label[0].toLowerCase()}`).join(' ');

  return (
    <div className="text-center">
      <p className="text-body font-body text-brand-primary mb-3">{label}</p>
      {/* Compact text on xs screens */}
      <div className="block sm:hidden text-heading-3 font-heading-3 text-default-font tabular-nums">
        {compactText}
      </div>
      {/* Styled boxes on sm+ screens */}
      <div className="hidden sm:flex items-center justify-center gap-2">
        {units.map((unit, i) => (
          <React.Fragment key={unit.label}>
            {i > 0 && (
              <span className="text-heading-2 font-heading-2 text-brand-primary/50 -mt-4">:</span>
            )}
            <div className="bg-default-background border border-brand-primary/20 rounded-md px-3 py-2 min-w-[56px]">
              <div className="text-heading-2 font-heading-2 text-default-font tabular-nums">
                {unit.value}
              </div>
              <div className="text-caption text-subtext-color">{unit.label}</div>
            </div>
          </React.Fragment>
        ))}
      </div>
    </div>
  );
};
